import { AfterViewInit, Component, ElementRef, HostListener, Input, OnDestroy, OnInit, QueryList, ViewChild, ViewChildren, ChangeDetectionStrategy, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Observable, of, switchMap, tap } from 'rxjs';

import { ChatEvent, Message, Participant, Room } from '@shared/factories';
import { ChatEventsService, ChatService, NotificationService, WebsocketService, UserService } from '@shared/services';
import { MessageModel } from '@shared/models';
import { ChatBoxMessageComponent } from './chat-box-message/chat-box-message.component';

@Component({
  selector:      'chat-box',
  templateUrl: './chat-box.component.html',
  host: { class: 'am-flex-column am-flex-1 overflow' },
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class ChatBoxComponent implements OnInit, AfterViewInit, OnDestroy {
  private destroyRef = inject(DestroyRef);
  @Input() chatFullAccess: boolean;
  @Input() room:  Room;

  roomName:       string;
  messagesCount:  number;
  messages:       Message[];

  participants:   Participant[];
  meParticipant:  Participant;
  owner:          Participant;
  getParticipantByIdFn: Function = this.getParticipantById.bind(this);

  messageInput:   string;
  editMode:       number;

  active:         boolean = true;
  loaded:         boolean;
  loading:        boolean;
  pendingScroll:  boolean;

  startTyping:     Date;
  meTyping:        Date;

  chatBoundary:   number;
  scrollPosition: any;
  unreadWatcher:  NodeJS.Timeout;
  markAsRead:     Set<number> = new Set();

  @ViewChild('chatBox')    chatBox:      ElementRef;
  @ViewChildren('message') messagesList: QueryList<ChatBoxMessageComponent>;

  @HostListener('document:click', ['$event'])
  activeHandler(event: Event): void {
    if (this.loaded) {
      if (this.eRef.nativeElement.contains(event.target)) this.active = true;
      else                                                this.active = false;
    }
  }

  @HostListener('document:keydown.enter', ['$event'])
  onEnterKeyHandler(event: KeyboardEvent) {
    if (this.active && this.visible) this.submit();
  }

  constructor(
    private eRef:                ElementRef,
    private userService:         UserService,
    private notificationService: NotificationService,
    private websocketService:    WebsocketService,
    private chatService:         ChatService,
    private chatEventsService:   ChatEventsService,
  ) { }

  ngOnInit(): void {
    this.loading = true;
    of(null).pipe(
      tap(() => this.prepareRoom()),
      switchMap(() => {
        if (this.messagesCount > 10) return this.loadMessages()
        return of(null);
      }),
      switchMap(() => this.joinRoom()),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(
      () => {
        this.websocketService.subscribeToRoom(this.room.id);
        setTimeout(() => {
          this.scrollToBottom();
          setTimeout(() => this.onScroll());
          this.loading = null;
        });
      },
      err => {
        this.notificationService.alert(err);
        this.loading = null;
      }
    );

    this.chatEventsService.chatEvents.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      event => {
        if (event?.channel === 'Chat::RoomsChannel' && event?.roomId === this.room.id) this.eventHandler(event);
      },
      err => console.log(err)
    );
  }

  ngOnDestroy(): void {
    clearInterval(this.unreadWatcher);
    this.websocketService.unsubscribeFromRoom(this.room.id);
  }

  get visible(): boolean {
    return !!this.eRef.nativeElement.offsetParent;
  }

  show(): void {
    this.focusInput();
    if (this.pendingScroll) {
      this.pendingScroll = false;
      setTimeout(() => { this.scrollToBottom(true); this.onScroll(); });
    }
  }

  private prepareRoom(): void {
    this.roomName      = this.room?.roomName;
    this.participants  = this.room?.participants;
    this.messagesCount = this.room?.messagesCount;
    this.messages      = [...this.room?.messages?.sort((a,b) => +a.seq - +b.seq)];
    this.owner         = this.participants.find(p => p.isOwner);
  }

  private joinRoom(): Observable<Participant> {
    return of(null).pipe(
      switchMap(() => {
        let meParticipant = this.participants.find(p => p.userId === this.userService.currentUserValue.id);
        if (!meParticipant || meParticipant.deletedAt) return this.chatService.joinRooom(this.room.id).pipe(tap(p => this.addMeToParticipants(p)));
        return of(meParticipant);
      }),
      tap(p => this.meParticipant = p)
    );
  }

  private addMeToParticipants(participant: Participant): void {
    if (!participant) return;
    if (!participant.userId) participant.userId = this.userService.currentUserValue.id;

    this.participants = this.room.participants;
    let index = this.participants.findIndex(p => p.id === participant.id || p.userId === participant.userId);
    if (index >= 0) this.participants[index] = participant;
    else            this.participants.push(participant);

    this.chatEventsService.updateRooms([this.room]);
  }

  ngAfterViewInit(): void {
    this.chatBoundary = this.chatBox.nativeElement.getBoundingClientRect().top;
    this.scrollPosition = {
      height:    this.chatBox?.nativeElement?.scrollHeight,
      scrollTop: this.chatBox?.nativeElement?.scrollTop
    };
    this.unreadWatcher = setInterval(() => this.markAsReadHandler(), 500);
  }

  loadMessagesHandler(): void {
    let load        = this.messagesCount - this.messages.length;
    let currentPage = Math.floor(this.messages.length / 10);

    if (load > 1) {
      this.loading = true;
      this.scrollPosition = {
        height: this.chatBox?.nativeElement?.scrollHeight,
        top:    this.chatBox?.nativeElement?.scrollTop
      };
      this.loadMessages(currentPage+1).subscribe();
    }
  }

  private loadMessages(page: number = 1): Observable<Message[]> {
    return this.chatService.loadMessages(this.room.id, page).pipe(
      tap(messages => this.updateMessages(messages))
    );
  }

  private updateMessages(messages: Message[]): void {
    let temp: any = {};
    this.messages.map(m => m.toJSON()).forEach(m => { temp[m.id] = m; });
         messages.map(m => m.toJSON()).forEach(m => { temp[m.id] = m; });
    this.messages = (Object.values(temp) as MessageModel[]).map(m => new Message(m, m.myParticipantId)).sort((a,b) => a.seq - b.seq);

    if (this.scrollPosition) setTimeout(() => {
      let box       = this.chatBox?.nativeElement;
      let height    = box.scrollHeight - this.scrollPosition.height;
      box.scrollTop = height + this.scrollPosition.top;
      this.loading = null;
    });
  }

  private markAsReadHandler(): void {
    if (this.markAsRead.size) {
      this.websocketService.markMessagesAsRead(this.room.id, [...this.markAsRead]);
      this.markAsRead.forEach(id => {
        let message = this.messages.find(m => m.id === id);
        if (message) {
          if (!message.viewerIds) message.viewerIds = [];
          message.viewerIds.push(this.meParticipant.id);
        }
      });
      this.markAsRead.clear();
    }
  }

  onScroll(): void {
    this.messagesList?.filter(m => !m.message?.viewerIds?.includes(this.meParticipant?.id)).forEach((m, index) => {
      let { top } = m.content.nativeElement.getBoundingClientRect();
      if (top >= this.chatBoundary) this.markAsRead.add(m.message.id);
    });
  }

  private scrollToBottom(force: boolean = null): void {
    if (force || !this.loaded) {
      this.focusInput();
      this.chatBox.nativeElement.scrollTop = this.chatBox.nativeElement.scrollHeight;

      this.active = true;
      this.loaded = true;
    }
  }

  focusInput(): void {
    this.eRef.nativeElement.querySelector('input').focus();
    setTimeout(() => this.active = true);
  }

  private eventHandler(event: ChatEvent): void {
    switch (event.type) {
      case 'new_message':         { this.messageReceived(event);                     break; };
      case 'message_updated':     { this.messageUpdated(event);                      break; };
      case 'seen_messages':       { this.messageRead(event);                         break; };
      case 'message_deleted':     { this.messageDeleted(event);                      break; };
      case 'participant_online':  { this.setOnlineState(event.participantId, true);  break; };
      case 'participant_offline': { this.setOnlineState(event.participantId, false); break; };
      default:
        break;
    };
  }

  private setOnlineState(participantId: number, state: boolean = null): void {
    let participant = this.getParticipantById(participantId);
    if (participant) participant.isOnline = state;
  }

  private messageReceived(event: ChatEvent): void {
    if (!this.messages?.find(m => m.id === event.id)) {
      this.messages.push(new Message({
        id:              event.id,
        participant_id:  event.participantId,
        bulk_message_id: event.bulkMessageId,
        created_at:      new Date().toISOString(),

        seq:             event.seq,
        text:            event.text,
        viewer_ids:      []
      }, this.userService.currentUserValue.id));
      this.messagesCount++;
      setTimeout(() => this.visible ? this.scrollToBottom(true) : this.pendingScroll = true);
    }
  }

  private messageUpdated(event: ChatEvent): void {
    let message = this.messages.find(m => m.id === event.id);
    if (message) {
      message.text     = event.text;
      message.editedAt = new Date();
    }
  }

  private messageDeleted(event: ChatEvent): void {
    let index = this.messages.findIndex(m => m.id === event.id);
    if (index !== -1) {
      this.messages[index] = new Message(Object.assign(
        this.messages[index].toJSON(),
        { deleted_at: new Date().toISOString() }
      ), this.messages[index].myParticipantId);
    }
  }

  private messageRead(event: ChatEvent): void {
    event?.messageIds?.forEach(id => {
      let temp = this.messages.find(m => m.id === id);
      if (temp) temp.viewerIds.push(event.participantId);
    });
  }

  private getParticipantById(participantId: number): Participant {
    return this.participants.find(p => p.id === participantId);
  }

  showDate(message: Message, index: number): boolean {
    return !index || this.messages[index-1]?.createdAt.getDate() !== message.createdAt.getDate();
  }

  getName(participantId: number): string {
    return this.participants?.find(p => p.id === participantId)?.fullName;
  }

  getDateTitle(message: Message): string {
    let today     = new Date().toISOString().slice(0, 10);
    let yesterday = new Date(new Date().setDate(new Date().getDate() - 1)).toISOString().slice(0, 10);;
    if (message.dateString === today    ) return 'Heute';
    if (message.dateString === yesterday) return 'Gestern';
    return message.createdAt.toLocaleDateString('de-DE', {
      weekday: 'long',
      year:    'numeric',
      month:   'long',
      day:     'numeric',
    }).replace('.', '');
  }

  submit(): void {
    this.postMessage().subscribe(
      () => {
        this.messageInput = null;
        this.loading      = null;
      },
      err => {
        this.notificationService.alert(err);
        this.loading = null;
      }
    );
  }

  private postMessage(): Observable<Message> {
    if (this.room.id && this.messageInput) {
      this.loading = true;
      return this.chatService.postMessage(this.room.id, this.messageInput);
    }
    return of(null);
  }

  updateMessageHandler(message: Message): void {
    this.editMode = message.id;
    this.loading = true;
    this.chatService.updateMessage(this.room.id, message.id, message.text).subscribe(
      message => {
        let index = this.messages.findIndex(m => m.id === message.id);
        if (index >= 0) this.messages[index] = message;

        this.editMode = null;
        this.loading  = null;
      },
      err => {
        this.notificationService.alert(err);
        this.loading = null;
      }
    );
  }

  deleteMessage(message: Message): void {
    this.chatService.deleteMessage(this.room.id, message.id).subscribe();
  }

  meTypingHandler(): void {
    this.meTyping = new Date();
    if (!this.startTyping || this.meTyping.getTime() - this.startTyping.getTime() > 3000) {
      this.startTyping = this.meTyping;
      this.websocketService.startTyping(this.room.id);
    }
  }

}
